import { Link } from 'react-router'
import { Icon } from '#app/components/ui/icon.tsx'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { getBlogPostListItems } from '#app/utils/mdx.server.ts'
import { getBlogListStructuredData } from '#app/utils/mdx.tsx'
import { type Route } from './+types/index.ts'

export const meta: Route.MetaFunction = () => {
	const title = 'Blog | MyBillingsMethod'
	const description =
		'Articles about natural family planning, the Billings Ovulation Method, fertility awareness, and privacy-first health tracking.'

	return [
		{ title },
		{ name: 'description', content: description },
		{
			name: 'keywords',
			content:
				'Billings Method, natural family planning, fertility awareness, NFP, cervical mucus tracking, fertility charting',
		},
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:type', content: 'website' },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const posts = await getBlogPostListItems()
	const siteUrl = getDomainUrl(request)

	return { posts, siteUrl }
}

export default function BlogIndex({ loaderData }: Route.ComponentProps) {
	const { posts, siteUrl } = loaderData
	const structuredData = getBlogListStructuredData(posts, siteUrl)

	return (
		<>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
			/>

			<div className="min-h-screen bg-stone-50">
				<Navigation />

				<main className="mx-auto max-w-3xl px-6 py-16">
					{/* Header */}
					<header className="mb-16 text-center">
						<h1 className="mb-4 font-serif text-4xl font-light tracking-tight text-stone-900 md:text-5xl">
							Journal
						</h1>
						<p className="mx-auto max-w-xl text-lg text-stone-600">
							Thoughts on fertility awareness, the Billings Method, and taking
							ownership of your health data.
						</p>
					</header>

					{/* Posts */}
					{posts.length > 0 ? (
						<div className="space-y-12">
							{posts.map((post) => (
								<article
									key={post.slug}
									className="group border-b border-stone-200 pb-12 last:border-0"
								>
									<Link
										to={`/blog/${post.slug}`}
										className="block"
										prefetch="intent"
									>
										{/* Date and reading time */}
										<div className="mb-3 flex items-center gap-3 text-sm text-stone-500">
											<time dateTime={post.frontmatter.date}>
												{post.dateDisplay}
											</time>
											<span className="text-stone-300">·</span>
											<span>{post.readTime.text}</span>
										</div>

										{/* Title */}
										<h2 className="mb-3 font-serif text-2xl font-medium text-stone-900 transition-colors group-hover:text-purple-700 md:text-3xl">
											{post.frontmatter.title}
										</h2>

										{/* Description */}
										<p className="mb-4 leading-relaxed text-stone-600">
											{post.frontmatter.description}
										</p>

										{/* Categories */}
										{post.frontmatter.categories.length > 0 && (
											<div className="flex flex-wrap gap-2">
												{post.frontmatter.categories.map((category) => (
													<span
														key={category}
														className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-600"
													>
														{category}
													</span>
												))}
											</div>
										)}
									</Link>
								</article>
							))}
						</div>
					) : (
						<div className="py-20 text-center">
							<div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-stone-100">
								<Icon name="pencil-1" className="h-8 w-8 text-stone-400" />
							</div>
							<h2 className="mb-2 text-xl font-medium text-stone-900">
								Coming soon
							</h2>
							<p className="text-stone-600">
								We're working on our first articles. Check back soon.
							</p>
						</div>
					)}
				</main>

				<Footer />
			</div>
		</>
	)
}

function Navigation() {
	return (
		<nav className="border-b border-stone-200 bg-stone-50/80 backdrop-blur-sm">
			<div className="mx-auto max-w-3xl px-6 py-4">
				<div className="flex items-center justify-between">
					<Link
						to="/"
						className="flex items-center space-x-2 text-stone-900 transition-colors hover:text-purple-700"
					>
						<Icon name="lock-closed" className="h-5 w-5 text-purple-600" />
						<span className="font-medium">MyBillingsMethod</span>
					</Link>
					<Link
						to="/"
						className="text-sm text-stone-600 transition-colors hover:text-stone-900"
					>
						Back to home
					</Link>
				</div>
			</div>
		</nav>
	)
}

function Footer() {
	return (
		<footer className="border-t border-stone-200 bg-stone-50 py-12">
			<div className="mx-auto max-w-3xl px-6">
				<div className="flex flex-col items-center justify-between gap-4 text-sm text-stone-500 sm:flex-row">
					<p>&copy; {new Date().getFullYear()} MyBillingsMethod</p>
					<div className="flex gap-6">
						<Link to="/privacy" className="hover:text-stone-900">
							Privacy
						</Link>
						<Link to="/tos" className="hover:text-stone-900">
							Terms
						</Link>
						<Link to="/support" className="hover:text-stone-900">
							Support
						</Link>
					</div>
				</div>
			</div>
		</footer>
	)
}
